import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { fmtCompetencia, fmtDateTime } from '../../lib/format';
import { ArrowLeft } from 'lucide-react';

interface Detalhe {
  arquivo: {
    id: string;
    nomeOriginal: string;
    competencia: string;
    versao: number;
    status: string;
    totalLinhas: number;
    hashArquivo: string;
    createdAt: string;
    processedAt: string | null;
    caminhoStorage: string;
  };
  breakdown: Record<string, { qtd: number; total: number; descricao: string }>;
  primeiras500Linhas: Array<{
    id: string;
    numeroLinha: number;
    matriculaRaw: string | null;
    cpfRaw: string | null;
    nomeRaw: string | null;
    valorDescontado: string | number | null;
    statusLinha: string;
    motivo: string | null;
    payloadOriginal: { statusEtipi?: string; cargo?: string };
  }>;
}

export function ImportacaoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['importacao', id],
    queryFn: () => api<Detalhe>(`/importacao/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <Spinner />;
  if (!data) return <p>Importação não encontrada</p>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/importacao" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" />voltar para importações
        </Link>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">{fmtCompetencia(data.arquivo.competencia)}</h1>
        <p className="text-muted-foreground">{data.arquivo.nomeOriginal} · versão {data.arquivo.versao}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(data.breakdown).sort(([a],[b]) => a.localeCompare(b)).map(([statusEtipi, info]) => {
          const isPaid = statusEtipi === '1' || statusEtipi === '4';
          return (
            <Card key={statusEtipi} className={isPaid ? 'border-green-200' : 'border-amber-200'}>
              <CardHeader className="pb-3">
                <CardDescription className="font-mono text-xs">Status {statusEtipi}</CardDescription>
                <CardTitle className="text-2xl">{info.qtd.toLocaleString('pt-BR')}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2 min-h-[2.5em]">{info.descricao}</p>
                <p className="text-sm font-medium">R$ {info.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detalhes do arquivo</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><dt className="text-muted-foreground">Total de linhas processadas</dt><dd className="font-medium">{data.arquivo.totalLinhas.toLocaleString('pt-BR')}</dd></div>
            <div><dt className="text-muted-foreground">Status</dt><dd className="font-medium">{data.arquivo.status}</dd></div>
            <div><dt className="text-muted-foreground">Enviado em</dt><dd className="font-medium">{fmtDateTime(data.arquivo.createdAt)}</dd></div>
            <div><dt className="text-muted-foreground">Processado em</dt><dd className="font-medium">{fmtDateTime(data.arquivo.processedAt)}</dd></div>
            <div className="md:col-span-2"><dt className="text-muted-foreground">Hash SHA-256</dt><dd className="font-mono text-xs break-all">{data.arquivo.hashArquivo}</dd></div>
            <div className="md:col-span-2"><dt className="text-muted-foreground">Caminho no storage</dt><dd className="font-mono text-xs break-all">{data.arquivo.caminhoStorage}</dd></div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Linhas (primeiras {data.primeiras500Linhas.length})</CardTitle>
          <CardDescription>Cada linha do arquivo, com status ETIPI nativo</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 pr-3">#</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-left py-2 pr-3">Matrícula</th>
                <th className="text-left py-2 pr-3">Nome</th>
                <th className="text-left py-2 pr-3">CPF</th>
                <th className="text-right py-2 pr-3">Valor</th>
                <th className="text-left py-2">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {data.primeiras500Linhas.map((l) => {
                const statusEtipi = l.payloadOriginal?.statusEtipi ?? '?';
                const isPaid = statusEtipi === '1' || statusEtipi === '4';
                return (
                  <tr key={l.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-1.5 pr-3 text-muted-foreground">{l.numeroLinha}</td>
                    <td className="py-1.5 pr-3"><span className={`font-mono text-xs px-1.5 py-0.5 rounded ${isPaid ? 'bg-green-100 text-green-900' : 'bg-amber-100 text-amber-900'}`}>{statusEtipi}</span></td>
                    <td className="py-1.5 pr-3 font-mono">{l.matriculaRaw}</td>
                    <td className="py-1.5 pr-3">{l.nomeRaw}</td>
                    <td className="py-1.5 pr-3 font-mono">{l.cpfRaw}</td>
                    <td className="py-1.5 pr-3 text-right">{l.valorDescontado != null ? Number(l.valorDescontado).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '—'}</td>
                    <td className="py-1.5 text-muted-foreground text-xs">{l.motivo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
